import {
  Input,
  InputNumber,
  Form,
  Select,
  Card,
  message,
  Flex,
  Checkbox,
  ColorPicker,
  Table,
  Button,
  Popconfirm,
  Divider,
  Tooltip,
} from 'antd';
import { useContext, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthenticationContext from '../../contexts/auth/AuthenticationContext';
import {
  handleErrorInputMessage,
  handleInputError,
} from '../../../core/utils/errorUtils';
import {
  FormCard,
  FormContainer,
  FormSectionTitle,
  READ_ONLY_INPUT_STYLE,
} from '@leedpulse-monorepo/components';
import {
  creditTypeAsString,
  EProductType,
  ContextStatus,
  PlanFeaturesType,
  ECreditType,
  Currency,
  UserRole,
  PartnersType,
  partnersTypeAsString,
  billingIntervalAsString,
} from '@leedpulse-monorepo/enums';
import { IPlan } from '@leedpulse-monorepo/interfaces';
import PlansContext from '../../contexts/data/PlansContext';
import PlanEditionFormContext from '../../contexts/forms/plans/PlanEditionFormContext';
import { PLANS_PAGE_PATH } from '../../pages/plans/PlansPage';
import PlanLinksList from './PlanLinksList';
import DebouncedPlansSelect from './select/DebouncedPlansSelect';
import { SyncOutlined, DeleteOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { SUBSCRIPTIONS_PAGE_PATH } from '../../pages/subscriptions/SubscriptionsPage';
import { advancedOnRow } from '../../../core/utils/commonUtils';
import { FirestoreCollections } from '@leedpulse-monorepo/firebase-metadata';
import LeedbucksRatesList from '../@shared/leedbucks/LeedbucksRatesList/LeedbucksRatesList';
import LeedbucksRatesModalButton from '../@shared/leedbucks/LeedbucksRatesModalButton/LeedbucksRatesModalButton';
import LeedbucksTempTabs from '../@shared/leedbucks/LeedbucksTempTabs/LeedbucksTempTabs';
import LeedbucksStartingPeriod from '../@shared/leedbucks/LeedbucksStartingPeriod/LeedbucksStartingPeriod';
import isEqual from 'lodash/isEqual';

const { Option } = Select;

const LEGACY_CREDIT_KEYS: ECreditType[] = [ECreditType.sms, ECreditType.minutes, ECreditType.contacts];

interface PlanDetailsProps {
  planDoc: IPlan;
}

const PlanDetails: React.FC<PlanDetailsProps> = ({ planDoc }) => {
  const navigate = useNavigate();
  const planEditionFormContext = useContext(PlanEditionFormContext);
  const plansContext = useContext(PlansContext);

  const authContext = useContext(AuthenticationContext);

  useEffect(() => {
    if (planEditionFormContext.wipPlan === null) {
      planEditionFormContext.initWithPlan(planDoc);
    }
  }, []);

  useEffect(() => {
    switch (planEditionFormContext.updateStatus) {
      case ContextStatus.SUCCESS:
        plansContext.fetch(undefined, true);
        message.success('Mise à jour du plan avec succès');
        break;
      case ContextStatus.ERROR:
        message.error('Erreur lors de la mise à jour du plan');
        break;
      default:
        break;
    }
  }, [planEditionFormContext.updateStatus]);

  useEffect(() => {
    switch (planEditionFormContext.deleteStatus) {
      case ContextStatus.SUCCESS:
        message.success('Plan supprimé avec succès');
        navigate(PLANS_PAGE_PATH);
        break;
      case ContextStatus.ERROR:
        message.error('Erreur lors de la suppression du plan');
        break;
      default:
        break;
    }
  }, [planEditionFormContext.deleteStatus]);

  const isBusy =
    planEditionFormContext.updateStatus === ContextStatus.LOADING ||
    planEditionFormContext.deleteStatus === ContextStatus.LOADING ||
    planEditionFormContext.syncStripeStatus === ContextStatus.LOADING;

  const isReadOnly =
    authContext.authentication == null ||
    ![UserRole.owner, UserRole.admin].includes(
      authContext.authentication?.role
    );
  // Promotions are owner-only, temporary rates and starting period: the others see them read-only
  const isPromoReadOnly = isReadOnly || authContext.authentication?.role !== UserRole.owner;

  const wipPlan = planEditionFormContext.wipPlan;

  // Prevent from reset the debouncing select and fetch at every char changed in the form (context issue)
  const notAllowedForDowngrade = useMemo(
    () => (wipPlan ? [wipPlan.id, ...(wipPlan.upgradable_to ?? [])] : []),
    [wipPlan?.id, wipPlan?.upgradable_to]
  );

  // Prevent from reset the debouncing select and fetch at every char changed in the form (context issue)
  const notAllowedForUpgrade = useMemo(
    () => (wipPlan ? [wipPlan.id, ...(wipPlan.downgradable_to ?? [])] : []),
    [wipPlan?.id, wipPlan?.downgradable_to]
  );

  if (wipPlan === null) {
    return <Card loading />;
  }

  return (
    <Flex vertical gap={16}>
      <FormCard
        title={planEditionFormContext.currentId}
        collectionName={FirestoreCollections.plans}
        onDeleted={
          isReadOnly ? undefined : () => planEditionFormContext.submitDelete()
        }
        onUpdated={
          isReadOnly ? undefined : () => planEditionFormContext.submitUpdate()
        }
        isUpdateLoading={isBusy}
        isDeleteLoading={isBusy}
        headerActions={
          isReadOnly ? undefined :
            <Flex gap={"0.5rem"} align="center">
              <Form.Item
                key="archived"
                style={{ marginBottom: 0 }}
              >
                <Checkbox
                  disabled={isBusy || isReadOnly}
                  defaultChecked={wipPlan.archived ?? false}
                  onChange={(e) =>
                    planEditionFormContext.onArchivedChange(e.target.checked)
                  }
                >
                  Archivé
                </Checkbox>
                <Tooltip title="Un plan archivé n'est plus proposé nulle part, ni dans le back-office ni côté client">
                  <QuestionCircleOutlined />
                </Tooltip>
              </Form.Item>
              <Popconfirm
                key="sync-stripe"
                title="Attention !"
                description="Cela va écraser les modifications locales, sauvegardez avant de continuer"
                onConfirm={() => planEditionFormContext.submitSyncStripe()}
              >
                <Button
                  type="default"
                  icon={<SyncOutlined />}
                  loading={isBusy}
                  disabled={isBusy || isReadOnly || !wipPlan.price.stripe_price_id}
                >
                  Sync Stripe
                </Button>
              </Popconfirm>
            </Flex>
        }
      >
        <Form layout="vertical" requiredMark={false}>
          <FormSectionTitle title="Informations générales" />
          <FormContainer>
            <Form.Item
              required
              label="ID"
              validateStatus={handleInputError(
                wipPlan.id,
                planEditionFormContext.updateStatus
              )}
              help={handleErrorInputMessage(
                wipPlan.id,
                planEditionFormContext.updateStatus
              )}
            >
              <Input
                readOnly // Will unsync stripe for now, be cautious /!\
                disabled={isBusy}
                // readOnly={isReadOnly}
                style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                onChange={(e) => {
                  // Only scack case, only [a-b] and [0-9] allowed
                  // Remove spaces
                  e.target.value = e.target.value.replace(/\s/g, '');
                  // Accept only A to Z, 0 to 9 and -
                  e.target.value = e.target.value.replace(/[^a-z0-9-]/g, '');
                  planEditionFormContext.onIdChange(e.target.value);
                }}
                value={wipPlan.id}
                status={wipPlan.id === '' ? 'error' : ''}
              />
            </Form.Item>

            <Form.Item
              required
              label="Nom"
              validateStatus={handleInputError(
                wipPlan.name,
                planEditionFormContext.updateStatus
              )}
              help={handleErrorInputMessage(
                wipPlan.name,
                planEditionFormContext.updateStatus
              )}
            >
              <Input
                disabled={isBusy}
                readOnly={isReadOnly}
                style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                onChange={(e) =>
                  planEditionFormContext.onNameChange(e.target.value)
                }
                value={wipPlan.name}
                status={wipPlan.name === '' ? 'error' : ''}
              />
            </Form.Item>

            {/* Hexadecimal */}
            <Form.Item required label="Couleur">
              <Flex gap={8} align="center">
                <ColorPicker
                  disabled={isBusy}
                  style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                  onChange={(color) =>
                    planEditionFormContext.onColorChange(color.toHexString())
                  }
                  value={wipPlan.color}
                />

                <Input readOnly value={wipPlan.color} />
              </Flex>
            </Form.Item>
          </FormContainer>

          <FormContainer>


            <Form.Item
              label="Prix"
              extra={<span>Écriture formatée : {wipPlan.price.formatted}</span>}
              tooltip="Déprécié, utilisation du prix Stripe linké à la place"
            >
              <Flex gap={8}>
                <InputNumber
                  readOnly // Change it from Stripe /!\
                  disabled={isBusy}
                  // readOnly={isReadOnly}
                  style={isReadOnly ? READ_ONLY_INPUT_STYLE : { width: '100%' }}
                  onChange={(value) =>
                    planEditionFormContext.onPriceChange({ value: value ?? 0 })
                  }
                  value={wipPlan.price.value}
                  step={0.01}
                  min={0}
                  decimalSeparator="."
                />

                <Select
                  disabled // Change it from Stripe /!\
                  // disabled={isBusy}
                  style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                  value={wipPlan.price.currency}
                  onChange={(value) =>
                    planEditionFormContext.onPriceChange(
                      { currency: value as Currency }
                    )
                  }
                >
                  {Object.keys(Currency).map((key) => (
                    <Option key={key} value={Currency[key as Currency]}>
                      {Currency[key as Currency]}
                    </Option>
                  ))}
                </Select>
              </Flex>
            </Form.Item>

            <Form.Item
              label="Périodicité"
              extra="price.interval"
              tooltip="Ce que le prix achète, lu depuis Stripe. Une entreprise ne peut pas mélanger des numéros mensuels et annuels."
            >
              <Input
                readOnly // Comes from the Stripe price, like the amount above
                disabled={isBusy}
                style={READ_ONLY_INPUT_STYLE}
                value={wipPlan.price.interval ? billingIntervalAsString(wipPlan.price.interval) : 'Inconnue — resynchroniser le plan depuis Stripe'}
              />
            </Form.Item>

            <Form.Item label="Stripe Price ID">
              <Input
                disabled={isBusy}
                style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                onChange={(e) =>
                  planEditionFormContext.onPriceChange({ stripe_price_id: e.target.value })
                }
                value={wipPlan.price.stripe_price_id}
              />
            </Form.Item>

            <Form.Item label="Stripe Tax ID">
              <Input
                disabled={isBusy}
                style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                onChange={(e) =>
                  planEditionFormContext.onPriceChange({ stripe_tax_id: e.target.value })
                }
                value={wipPlan.price.stripe_tax_id ?? ''}
                placeholder="txr_... (optionnel)"
              />
            </Form.Item>
          </FormContainer>

          <Divider />

          <FormContainer>
            <Form.Item label="Produit">
              <Select
                disabled={isBusy}
                style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                value={wipPlan.product}
                onChange={(value) =>
                  planEditionFormContext.onProductChange(value as EProductType)
                }
              >
                <Option value={EProductType.LEEDPULSE}>Leedpulse</Option>
                <Option value={EProductType.LEEDFLOW}>LeedFlow</Option>
              </Select>
            </Form.Item>

            {wipPlan.product === EProductType.LEEDFLOW && (
              <>
                <Form.Item label="Downgradable vers">
                  <DebouncedPlansSelect
                    auth={authContext.authentication}
                    product={EProductType.LEEDFLOW}
                    mode="multiple"
                    showSearch
                    customOptions={false}
                    notAllowedOptions={notAllowedForDowngrade}
                    placeholder="Rechercher un plan..."
                    disabled={isBusy}
                    style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                    value={wipPlan.downgradable_to}
                    onChange={(value) => planEditionFormContext.onDowngradableToChange(value as string[])}
                  />
                </Form.Item>

                <Form.Item label="Upgradable vers">
                  <DebouncedPlansSelect
                    auth={authContext.authentication}
                    product={EProductType.LEEDFLOW}
                    mode="multiple"
                    showSearch
                    customOptions={false}
                    notAllowedOptions={notAllowedForUpgrade}
                    placeholder="Rechercher un plan..."
                    disabled={isBusy}
                    style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                    value={wipPlan.upgradable_to}
                    onChange={(value) => planEditionFormContext.onUpgradableToChange(value as string[])}
                  />
                </Form.Item>

                <Form.Item label="Partenaires" extra="partner_systems">
                  <Select
                    disabled={isBusy}
                    style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                    value={wipPlan.partner_systems}
                    onChange={(value) =>
                      planEditionFormContext.onPartnerSystemsChange(value as string[])
                    }
                    options={Object.entries(PartnersType).map(([key, value]) => (
                      { label: `${partnersTypeAsString(value as PartnersType)} (${key})`, value: key }
                    ))}
                    allowClear
                    mode="multiple"
                  />
                </Form.Item>
              </>
            )}
          </FormContainer>

          <Divider />

          <FormContainer>
            <Form.Item label="Features">
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: 8,
                  maxHeight: '6.25rem',
                  overflowY: 'auto',
                }}
              >
                {Object.keys(PlanFeaturesType).map((key) => (
                  <Flex key={key} gap={8} align="center">
                    <Checkbox
                      defaultChecked={wipPlan.features[key as PlanFeaturesType]}
                      onChange={(e) =>
                        planEditionFormContext.onFeaturesChange(
                          key as PlanFeaturesType,
                          e.target.checked
                        )
                      }
                    >
                      {PlanFeaturesType[key as PlanFeaturesType]}
                    </Checkbox>
                  </Flex>
                ))}
              </div>
            </Form.Item>
          </FormContainer>

          <FormContainer oneColumnMax>
            <Form.Item label="Liens">
              <PlanLinksList
                wipPlan={wipPlan}
                planEditionFormContext={planEditionFormContext}
              />
            </Form.Item>
          </FormContainer>

          <FormSectionTitle
            title={`Gestion des crédits`}
          />

          <FormContainer>
            <Form.Item label="Crédits">
              <Flex gap={8}>
                {
                  (
                    wipPlan.product === EProductType.LEEDFLOW ?
                      [ECreditType.leedbucks, ...LEGACY_CREDIT_KEYS.filter((k) => wipPlan.credits?.[k] !== undefined)] :
                      Object.keys(ECreditType).filter((key) => key !== ECreditType.leedbucks)
                  ).map((key) => {
                    const isLegacy = wipPlan.product === EProductType.LEEDFLOW && key !== ECreditType.leedbucks;
                    return (
                      <Flex key={key} gap={4} align="center">
                        <span title={creditTypeAsString(key, false)}>
                          {creditTypeAsString(key)}
                        </span>
                        <InputNumber
                          disabled={isBusy}
                          readOnly={isReadOnly}
                          status={isLegacy ? 'error' : undefined}
                          style={
                            isReadOnly ? READ_ONLY_INPUT_STYLE : { width: '100%' }
                          }
                          onChange={(value) =>
                            planEditionFormContext.onCreditsChange(
                              key as ECreditType,
                              value ?? 0
                            )
                          }
                          value={wipPlan.credits[key as ECreditType]}
                          step={1}
                          min={0}
                        />
                        {isLegacy && !isReadOnly && (
                          <Button
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            disabled={isBusy}
                            title="Supprimer ce crédit legacy (non utilisé par Leedflow)"
                            onClick={() => planEditionFormContext.onClearCredit(key as ECreditType)}
                          />
                        )}
                      </Flex>
                    );
                  })
                }
              </Flex>
            </Form.Item>

            {wipPlan.product === EProductType.LEEDFLOW &&
              <Form.Item label="Prix hors forfait">
                <Flex gap={8}>
                  {[ECreditType.leedbucks, ...LEGACY_CREDIT_KEYS.filter((k) => wipPlan.payg?.[k] !== undefined)].map((key) => {
                    const isLegacy = key !== ECreditType.leedbucks;
                    return (
                      <Flex key={key} gap={4} align="center">
                        <span title={creditTypeAsString(key, false)}>
                          {creditTypeAsString(key)}
                        </span>
                        <InputNumber
                          disabled={isBusy}
                          readOnly={isReadOnly}
                          status={isLegacy ? 'error' : undefined}
                          style={
                            isReadOnly ? READ_ONLY_INPUT_STYLE : { width: '100%' }
                          }
                          onChange={(value) =>
                            planEditionFormContext.onPaygChange(
                              key as ECreditType,
                              value ?? 0
                            )
                          }
                          value={wipPlan.payg?.[key as ECreditType] ?? 0}
                          step={0.01}
                          min={0}
                        />
                        {isLegacy && !isReadOnly && (
                          <Button
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            disabled={isBusy}
                            title="Supprimer ce prix hors forfait legacy (non utilisé par Leedflow)"
                            onClick={() => planEditionFormContext.onClearPayg(key as ECreditType)}
                          />
                        )}
                      </Flex>
                    );
                  })}
                </Flex>
              </Form.Item>
            }

            {wipPlan.product === EProductType.LEEDFLOW &&
              <Form.Item label="Promotions">
                <Flex gap={8} wrap="wrap">
                  <LeedbucksRatesModalButton
                    label="Configurer les taux"
                    title="Taux Leedbucks du plan"
                    isModified={!isEqual(wipPlan.leedbucks_rates ?? {}, planEditionFormContext.savedPlan?.leedbucks_rates ?? {})}
                  >
                    <LeedbucksRatesList
                      rates={wipPlan.leedbucks_rates ?? {}}
                      onChange={(rates) => planEditionFormContext.onLeedbucksRatesChange(rates)}
                      isReadOnly={isReadOnly}
                      canManageRows
                    />
                  </LeedbucksRatesModalButton>
                  <LeedbucksRatesModalButton
                    label="Configurer les taux temporaires"
                    title="Taux Leedbucks temporaires du plan"
                    isModified={!isEqual(wipPlan.leedbucks_rates_temp ?? [], planEditionFormContext.savedPlan?.leedbucks_rates_temp ?? [])}
                  >
                    <LeedbucksTempTabs
                      periods={wipPlan.leedbucks_rates_temp ?? []}
                      onChange={(periods) => planEditionFormContext.onLeedbucksRatesTempChange(periods)}
                      isReadOnly={isPromoReadOnly}
                      createPeriod={(id, ending_at) => ({ id, ending_at, rates: {} })}
                      renderPeriod={(period, update) => <LeedbucksRatesList
                        rates={period.rates}
                        onChange={(rates) => update({ rates })}
                        isReadOnly={isPromoReadOnly}
                        canManageRows
                      />}
                      emptyLabel="Aucun taux temporaire"
                    />
                  </LeedbucksRatesModalButton>
                  <LeedbucksRatesModalButton
                    label="Configurer la période de démarrage"
                    title="Période de démarrage de chaque nouvel abonnement"
                    isModified={!isEqual(wipPlan.starting_period, planEditionFormContext.savedPlan?.starting_period)}
                  >
                    <LeedbucksStartingPeriod
                      period={wipPlan.starting_period}
                      planLeedbucks={wipPlan.credits?.leedbucks ?? 0}
                      onChange={(period) => planEditionFormContext.onStartingPeriodChange(period)}
                      isReadOnly={isPromoReadOnly}
                    />
                  </LeedbucksRatesModalButton>
                </Flex>
              </Form.Item>
            }

            {wipPlan.product === EProductType.LEEDFLOW &&
              <Form.Item label="Engagement" tooltip="Les abonnements créés sur ce plan n'ont pas de date d'engagement">
                <Checkbox
                  disabled={isBusy || isReadOnly}
                  checked={wipPlan.no_commitment ?? false}
                  onChange={(e) => planEditionFormContext.onNoCommitmentChange(e.target.checked)}
                >
                  Sans engagement
                </Checkbox>
              </Form.Item>
            }
          </FormContainer>

          <FormContainer>
            <Form.Item label="Récurrence">
              <Checkbox
                defaultChecked={wipPlan.recurrence}
                onChange={(e) =>
                  planEditionFormContext.onRecurrenceChange(e.target.checked)
                }
              >
                Récurrence
              </Checkbox>
            </Form.Item>

            <Form.Item label="Réinitialisation des crédits">
              <Checkbox
                defaultChecked={wipPlan.recurrence_credits_reset}
                onChange={(e) =>
                  planEditionFormContext.onRecurrenceCreditsResetChange(
                    e.target.checked
                  )
                }
              >
                Réinitialisation des crédits
              </Checkbox>
            </Form.Item>
          </FormContainer>

        </Form>


        <FormSectionTitle
          title={`Abonnements (${wipPlan.subscriptions?.length ?? 0})`}
        />

        <Table
          dataSource={wipPlan.subscriptions ?? []}
          columns={[
            {
              title: 'Nom',
              dataIndex: 'name',
              key: 'name',
            },
          ]}
          onRow={(record) =>
            advancedOnRow({
              url: `${SUBSCRIPTIONS_PAGE_PATH}/${record.id}`,
              navigate,
              className: 'table-row',
            })
          }
          rowKey={(record) => record.id}
        />
      </FormCard>
    </Flex>
  );
};

export default PlanDetails;
